import React, { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { raffleAPI } from '@/lib/api';

/**
 * Search + add/remove the agents (by NPN) who work a Sales event. These
 * become the participating agents of the mirrored Event Request, so their
 * leads attribute to the event and count in its metrics.
 */
export default function SalesAgentPicker({ value = [], onChange }) {
  const [agents, setAgents] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    raffleAPI.assignableAgents()
      .then((r) => setAgents(r.data.agents || []))
      .catch(() => {});
  }, []);

  const nameByNpn = useMemo(() => {
    const m = {};
    agents.forEach((a) => { m[a.npn] = a.name; });
    return m;
  }, [agents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents.slice(0, 50);
    return agents.filter(
      (a) => (a.name || '').toLowerCase().includes(q) || (a.npn || '').includes(q),
    ).slice(0, 50);
  }, [agents, query]);

  const toggle = (npn) => {
    const next = value.includes(npn) ? value.filter((n) => n !== npn) : [...value, npn];
    onChange(next);
  };

  return (
    <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50/60 p-3" data-testid="sales-agents">
      <p className="text-xs font-medium text-slate-700 mb-1">Agents working this event</p>
      <p className="text-[11px] text-slate-500 mb-2">
        Add the agents who work this Sales event. Their leads will attribute to it and count in the event's metrics.
      </p>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="sales-agents-selected">
          {value.map((npn) => (
            <Badge
              key={npn}
              variant="secondary"
              className="flex items-center gap-1 bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
            >
              {nameByNpn[npn] || `#${npn}`}
              <button
                type="button"
                onClick={() => toggle(npn)}
                className="ml-0.5 hover:text-indigo-950"
                data-testid={`sales-agent-remove-${npn}`}
                aria-label={`Remove ${nameByNpn[npn] || npn}`}
              >
                <X size={11} />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search agents by name or NPN…"
          className="pl-8 h-8 text-sm bg-white"
          data-testid="sales-agent-search"
        />
      </div>

      <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
        {filtered.length === 0 && (
          <p className="p-3 text-xs text-slate-400">No agents match.</p>
        )}
        {filtered.map((a) => (
          <label
            key={a.npn}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
            data-testid={`sales-agent-${a.npn}`}
          >
            <Checkbox checked={value.includes(a.npn)} onCheckedChange={() => toggle(a.npn)} />
            <span className="truncate">{a.name}</span>
            <span className="text-[11px] text-slate-400 ml-auto">#{a.npn}</span>
          </label>
        ))}
      </div>

      {value.length > 0 && (
        <p className="text-[11px] text-indigo-700 mt-1">
          {value.length} agent{value.length === 1 ? '' : 's'} assigned to this event
        </p>
      )}
    </div>
  );
}
